///
/// EarthBrowser
///
/// Created by Matt Giger on 11/2/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Forecast radar layer: syncs the `radar_forecast` Arrow dataset (NOAA
/// RRFS 3 km composite reflectivity in 15-minute steps to +18 h, hourly
/// beyond, plus the REFS probability of > 40 dBZ echoes, published by
/// radar-services) and extends the radar timeline past the newest
/// observation. A sub-layer of the radar mosaic: visible only while the
/// "radar" dataset is on as well, since it hands off from the observed
/// frames and borrows the radar shader chunk.
///

import { type Database } from "@/earthbrowser/database/Database"
import { type Earth } from "@/earthbrowser/activities/EarthActivity/Earth"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { type RadarDatasetLayer } from "@/earthbrowser/dataset-distribution/radar/RadarDatasetLayer"
import { RadarForecastOverlay } from "@/earthbrowser/dataset-distribution/radar-forecast/RadarForecastOverlay"
import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { RadarTextureFrame } from "@/earthbrowser/dataset-distribution/mrms/RadarTextureFrame"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { isMobile } from "@/earthbrowser/utils/platform"
import { time, timedelta } from "@stovokor/math/time"
import { DatasetLayer, type DatasetDelta, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { cRadarForecastProductProb40, cRadarForecastProductRefc, RadarForecastTable, type RadarForecastFrameRecord } from "./RadarForecastTable"

// Frames valid earlier than this before now are left out: the forecast
// only draws past the newest observation, and a little overlap is enough
// for the hand-off and a stale observed feed
const cPastWindowMs = 3 * 3_600_000

// Mobile keeps the hourly reflectivity steps only (a quarter of the frames
// to fetch and decode; flow advection still carries the motion between them)
const cMobileStepMinutes = 60

/** Raw Arrow row shape for the forecast frames feed (only fields we read) */
interface RadarForecastRow {
	id: unknown
	product: unknown
	run_time: unknown
	valid_time: unknown
	forecast_minutes: unknown
	image_url: unknown
	source_path: unknown
	size_bytes: unknown
	width: unknown
	height: unknown
	vmin: unknown
	vmax: unknown
	bounds_wgs84: unknown
	created_at: unknown
}

/** "{-134.13,21.12,-60.87,52.65}" (Postgres array text) or JSON -> numbers */
function parseBounds(text: string): number[] | null {
	const values = text.replace(/[{}[\]\s]/g, "").split(",").map(Number)
	return values.length === 4 && values.every(Number.isFinite) ? values : null
}

export class RadarForecastDatasetLayer extends DatasetLayer<RadarForecastFrameRecord> {
	private overlay: RadarForecastOverlay
	private earth: Earth
	private radarLayer: RadarDatasetLayer
	// Cache frames by URL so a refresh reuses frames that are still listed
	private frameCache = new Map<string, RadarTextureFrame>()
	private unsubscribe: (() => void) | null = null

	constructor(context: EarthBrowserContext, earth: Earth, radarLayer: RadarDatasetLayer) {
		super(context, "radar_forecast")
		this.earth = earth
		this.radarLayer = radarLayer
		this.overlay = new RadarForecastOverlay(context)

		// Hand-off: the forecast waits for the newest observed frame, and
		// the observed mosaic (plus its refinement tiles) yields to the
		// forecast by the same weight while this layer is on
		this.overlay.observedEndMs = () => this.radarLayer.latestObservedMs()
		this.radarLayer.yieldToForecast = () => this.visible ? this.overlay.weight : 0

		const state = engineBridge.getState()
		this.visible = state ? RadarForecastDatasetLayer.wantsVisible(state.earthbrowser.visibleDatasets) : false
		if (this.visible) {
			this.earth.addOverlay(this.overlay)
		}

		this.unsubscribe = engineBridge.subscribe(() => {
			const current = engineBridge.getState()
			if (!current) return
			this.setVisible(RadarForecastDatasetLayer.wantsVisible(current.earthbrowser.visibleDatasets))
		})
	}

	/** The forecast is a sub-layer of the radar mosaic: both toggles must be on */
	private static wantsVisible(visibleDatasets: string[]): boolean {
		return visibleDatasets.includes("radar") && visibleDatasets.includes("radar_forecast")
	}

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "radar_forecast",
			pollIntervalMs: 120_000,
			needsSync: true,
			supportsDelta: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await RadarForecastTable.create(db)
	}

	public override getAvailableTimeRange(): { startMs: number; endMs: number } | null {
		const range = this.overlay.getTimeRange()
		return range ? { startMs: range.start.date.getTime(), endMs: range.end.date.getTime() } : null
	}

	public override getTimeLaneDetail(): TimeLaneDetail | null {
		return this.overlay.getTimeLaneDetail()
	}

	protected override frameLoadState(): LayerLoadState | null {
		return this.overlay.getLoadState()
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return
		const rows = await downloadArrowRows(this.resolveUrl(shard.url, shard.path))
		await RadarForecastTable.replaceAll(this.parseRows(rows))
	}

	public async applyDelta(
		delta: DatasetDelta,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const rows = await downloadArrowRows(this.resolveUrl(delta.upserts.url, delta.upserts.path))
		const upserts = this.parseRows(rows)
		if (upserts.length > 0) {
			await RadarForecastTable.upsert(upserts)
		}
	}

	private parseRows(rows: unknown[]): RadarForecastFrameRecord[] {
		return (rows as RadarForecastRow[]).map(r => ({
			id: String(r.id),
			product: String(r.product),
			run_time: new Date(String(r.run_time)).toISOString(),
			valid_time: new Date(String(r.valid_time)).toISOString(),
			forecast_minutes: Number(r.forecast_minutes),
			image_url: String(r.image_url),
			source_path: String(r.source_path),
			size_bytes: Number(r.size_bytes),
			width: Number(r.width),
			height: Number(r.height),
			vmin: Number(r.vmin),
			vmax: Number(r.vmax),
			bounds_wgs84: String(r.bounds_wgs84),
			created_at: new Date(String(r.created_at)).toISOString(),
		}))
	}

	public async queryFeatures(): Promise<RadarForecastFrameRecord[]> {
		return RadarForecastTable.getValidSince(new Date(Date.now() - cPastWindowMs).toISOString())
	}

	public createSceneObject(_feature: RadarForecastFrameRecord): DatasetSceneObject<RadarForecastFrameRecord> | undefined {
		// Frames are consumed by the overlay, not by scene objects
		return undefined
	}

	public async onSyncComplete(): Promise<void> {
		await this.refresh()
	}

	/** Re-read the frames from the database and hand them to the overlay */
	public override async refresh(): Promise<void> {
		try {
			const records = await this.queryFeatures()
			const step = isMobile() ? cMobileStepMinutes : 0
			const refl = records.filter(r => r.product === cRadarForecastProductRefc
				&& (step === 0 || r.forecast_minutes % step === 0))
			const prob = records.filter(r => r.product === cRadarForecastProductProb40)
			const frames = this.updateFrameCache([...refl, ...prob])
			this.overlay.setReflectivityFrames(refl.map(r => frames.get(r.image_url)!), this.sharedBounds(refl))
			this.overlay.setProbabilityFrames(prob.map(r => frames.get(r.image_url)!), this.sharedBounds(prob))
		} catch (error) {
			console.error("[RadarForecastDatasetLayer] Failed to refresh:", error)
		}
	}

	// Every frame of a product comes off the same reprojected grid, so one
	// extent serves the series (the newest record's, should the grid change)
	private sharedBounds(records: RadarForecastFrameRecord[]): number[] | null {
		const last = records[records.length - 1]
		return last ? parseBounds(last.bounds_wgs84) : null
	}

	/**
	 * Reuse cached frames for URLs still listed, create frames for new
	 * ones (regional: longitude clamps, no wrap) and purge the rest
	 */
	private updateFrameCache(records: RadarForecastFrameRecord[]): Map<string, RadarTextureFrame> {
		const wanted = new Set(records.map(r => r.image_url))
		for (const [url, frame] of this.frameCache) {
			if (!wanted.has(url)) {
				frame.purge()
				this.frameCache.delete(url)
			}
		}
		const frames = new Map<string, RadarTextureFrame>()
		for (const record of records) {
			let frame = this.frameCache.get(record.image_url)
			if (!frame) {
				// Each frame stands until the next one; the series blends
				// between neighbours so the nominal duration only matters
				// for the last frame's hold
				const duration = record.product === cRadarForecastProductRefc ? timedelta.minutes(15) : timedelta.hours(1)
				frame = new RadarTextureFrame(this.context, record.image_url, new time(record.valid_time), duration, false)
				this.frameCache.set(record.image_url, frame)
			}
			frames.set(record.image_url, frame)
		}
		return frames
	}

	/** Dev instrumentation (window.__ebDebug.getRadarForecast) */
	public debugSnapshot(): Record<string, unknown> {
		const observedEnd = this.radarLayer.latestObservedMs()
		return {
			visible: this.visible,
			weight: this.overlay.weight,
			observedEnd: observedEnd ? new Date(observedEnd).toISOString() : null,
			clock: this.context.clock.clockTime.date.toISOString(),
			reflectivityBounds: this.overlay.reflectivityBounds,
			reflectivity: this.overlay.reflectivitySeries.debugSnapshot(),
			probability: this.overlay.probabilitySeries.debugSnapshot(),
			frames: this.frameCache.size,
		}
	}

	public setVisible(visible: boolean): void {
		if (this.visible === visible) return
		this.visible = visible

		if (visible) {
			this.earth.addOverlay(this.overlay)
			void this.refresh()
		} else {
			this.earth.removeOverlay(this.overlay)
			// Reclaim GPU memory while hidden; frames reload from the SQLite
			// image cache when re-enabled
			this.overlay.purge()
			for (const frame of this.frameCache.values()) {
				frame.purge()
			}
		}
	}

	override dispose(): void {
		this.unsubscribe?.()
		this.unsubscribe = null
		this.radarLayer.yieldToForecast = () => 0
		for (const frame of this.frameCache.values()) {
			frame.purge()
		}
		this.frameCache.clear()
		this.earth.removeOverlay(this.overlay)
		super.dispose()
	}
}
